<template>
  <div class="flex flex-col">
    <BuildingToggleItem
      :item="{
        name: $t('admin.navigation.building_selector.all'),
        id: 'all',
      }"
      :size="size"
    >
      <Toggle
        v-for="setting of settings"
        :key="setting.key"
        v-model:pressed="controllerSettings[setting.key].value"
        @update:pressed="(val) => setAll(setting.key, val)"
        :intermediate="controllerSettings[setting.key].intermediate"
        :size="size"
        :aria-label="setting.label"
      >
        <Icon :name="setting.icon" size="xl" md-size="md" />
      </Toggle>
    </BuildingToggleItem>

    <div class="h-px w-full bg-quaternary my-2" />

    <div class="flex flex-col gap-1">
      <BuildingToggleItem
        v-for="building of buildings"
        :key="building.id"
        :item="building"
        :size="size"
      >
        <Toggle
          v-for="setting of settings"
          :key="setting.key"
          v-model:pressed="internalSettings[building.id][setting.key]"
          @update:pressed="(val) => changeSetting(building, setting.key, val)"
          :size="size"
          :aria-label="setting.label"
        >
          <Icon :name="setting.icon" size="xl" md-size="md" />
        </Toggle>
      </BuildingToggleItem>
    </div>
  </div>
</template>
<script setup>
import { ref } from "vue";
import { BuildingToggleItem } from "@/SharedComponents/BuildingToggleList";
import { Toggle } from "@/SharedComponents/Toggles";
import { Icon } from "@/SharedComponents/Icon";

const emit = defineEmits(["update:values"]);

const props = defineProps({
  buildings: { type: Array, required: true },
  settings: { type: Array, required: true },
  values: { type: Array, required: false },
  size: { type: String, required: false },
});

const internalSettings = ref({});
const controllerSettings = ref({});

for (const building of props.buildings) {
  for (const setting of props.settings) {
    controllerSettings.value[setting.key] = {
      value: false,
      intermediate: false,
    };
    internalSettings.value[building.id] =
      internalSettings.value[building.id] || {};
    const value = props.values?.find((v) => v.tenant_id === building.id)
      ?.settings[setting.key];
    internalSettings.value[building.id][setting.key] =
      value !== undefined ? value : setting.default || false;
  }
}

const changeSetting = (building, settingKey, value) => {
  internalSettings.value[building.id][settingKey] = value;
  updateControllerSetting(settingKey);
  emitValues();
};

const setAll = (settingKey, value) => {
  for (const building of props.buildings) {
    internalSettings.value[building.id][settingKey] = value;
  }
  updateControllerSetting(settingKey);
  emitValues();
};

const controllerValue = (settingKey) => {
  let checked = 0;
  let unchecked = 0;
  for (const building of props.buildings) {
    if (internalSettings.value[building.id][settingKey] === true) {
      checked++;
    } else if (internalSettings.value[building.id][settingKey] === false) {
      unchecked++;
    }
  }
  return {
    value: checked === props.buildings.length,
    intermediate: checked > 0 && unchecked > 0,
  };
};

const updateControllerSetting = (settingKey) => {
  controllerSettings.value[settingKey] = {
    ...controllerValue(settingKey),
  };
};

const emitValues = () => {
  const values = [];
  for (const building of props.buildings) {
    const settings = {};
    for (const setting of props.settings) {
      settings[setting.key] = internalSettings.value[building.id][setting.key];
    }
    values.push({ tenant_id: building.id, settings });
  }
  emit("update:values", values);
};

for (const setting of props.settings) {
  updateControllerSetting(setting.key);
}
</script>
